import { inviteFormType } from 'utils/constants';
import { activities, corporateForms } from 'utils/formData';
import { convertToBoolOrNull } from 'utils/helpers';

export const inviteUser = ({ firstName, lastName, email, roleId, phoneNumber, ...rest }, type) => ({
    title: rest.title,
    firstName: firstName.trim(),
    lastName: lastName.trim().toUpperCase(),
    email: email.trim().toLowerCase(),
    phoneNumber: phoneNumber,
    subscriptions: [{ role: `/api/roles/${rest.role}` }],
    ...(type === inviteFormType.CIF && {
        marketingCertified: rest.marketingCertified,
        tag: rest.tag,
    }),
    ...(type === inviteFormType.PROVIDER && { sgp: `/api/sgps/${rest.sgp}` }),
    ...(type === inviteFormType.PROMOTER && { promoter: `/api/promoters/${rest.promoter}` }),
});

export const impersonate = userId => ({
    impersonatedUser: `/api/users/${userId}`,
});

export const getUsers = payload => ({
    filter_id: payload.id,
    filter_roles: payload.role,
    filter_networkAdmin: payload.networkAdmin,
    pagination: payload.pagination,
    page: payload.page,
    enrich_with: payload.enrichWith,
    filter_subscriptionStatus: payload.subscriptionStatus,
    filter_companyStatus: payload.companyStatus,
    order_by: payload.orderBy,
    order_direction: payload.direction?.toUpperCase(),
    filter_emails: [payload.email?.toLowerCase()],
    filter_alertTypes: payload?.alertTypes,
    filter_shareSubcategories: payload?.subcategory,
    filter_investmentFunds: payload?.product,
    filter_companyId: payload?.companyId,
    filter_investorClassification: payload?.investorClassification,
    filter_maxAdmissionFee: payload?.maxAdmissionFee,
    filter_minAdmissionFee: payload?.minAdmissionFee,
    filter_accountCreationToDate: payload?.entryDateEnd,
    filter_accountCreationFromDate: payload?.entryDateStart,
    filter_investingSumMin: payload?.minSum,
    filter_investingSumMax: payload?.maxSum,
    filter_search: payload.keyword,
    filter_subscriptionCreatedBy: payload?.dist,
    filter_subscriptionCreatedByTag: payload?.tag,
    filter_fundSGPId: payload.fundSGPId || (payload.provider ? [payload.provider] : undefined),
    filter_promoterId: payload.promoter,
});

export const updateAddressPayload = payload => ({
    currentLocation: {
        country: payload.address.country,
        address: payload.address.address,
        zipCode: payload.address.zipCode,
        city: payload.address.city,
        formattedAddress: payload.address.formattedAddress,
    },
});

export const updatePhoneNumberPayload = payload => ({
    phoneNumber: payload.phoneNumber,
});

export const updatePassword = payload => ({
    oldPassword: payload.oldPassword,
    newPassword: payload.newPassword,
});

export const updateInvestorProfilePP = payload => {
    const taxLocation = payload.hasDifferentFiscalAddress
        ? {
              id: `/api/locations/${payload.fiscalAddress.id}`,
              country: payload.fiscalAddress.country,
              address: payload.fiscalAddress.address,
              city: payload.fiscalAddress.city,
              zipCode: payload.fiscalAddress.zipCode,
              formattedAddress: payload.fiscalAddress.formattedAddress,
          }
        : {
              id: `/api/locations/${payload?.fiscalAddress?.id}`,
              country: null,
              address: '',
              city: '',
              zipCode: '',
              formattedAddress: null,
          };

    const foreignTaxObligation = JSON.stringify({
        choice: payload.fiscalSituation.fiscalObligationOtherCountry,
        fields:
            payload.fiscalSituation.fiscalObligationOtherCountry === 'true'
                ? payload.fiscalObligations.map(fo => ({
                      country: fo.country,
                      nif: fo.nif,
                      nifReason: fo.nifReason,
                  }))
                : [],
    });

    const conditions = payload?.conditions
        ? {
              termsAgreement: payload.conditions.termsAgreement,
              emailAgreement: payload.conditions.acceptedEmails,
              subscribedToNewsletter: payload.conditions.subscribedToNewsletter,
          }
        : {};

    return {
        firstName: payload.generalInfo.firstName,
        lastName: payload.generalInfo.lastName.toUpperCase(),
        phoneNumber: payload.generalInfo.phoneNumber,
        title: payload.generalInfo.title,
        birthName: payload.generalInfo.birthName,
        email: payload.generalInfo.email,
        investorProfile: {
            id: `/api/investor_profiles/${payload.generalInfo.id}`,
            birthDate: payload.generalInfo.birthDate || null,
            nationality: payload.generalInfo.nationality,
            jobTitle: payload.situation.profession,
            usPerson: payload.fiscalSituation.isUSPerson === 'ineligible',
            taxCountry: payload.fiscalSituation.taxResident,
            activity: payload.situation.activitySector,
            maritalStatus: payload.situation.situation,
            numberOfDependents: parseInt(payload.situation.noOfPersons),
            nif: payload.fiscalSituation.nif,
            matrimonialRegime: payload.situation.matrimonialRegime,
            usTaxResidency: convertToBoolOrNull(payload.fiscalSituation.wasUSFiscalResident),
            tin: payload.fiscalSituation.tin,
            nifReason: payload.fiscalSituation.nifReason,
            foreignTaxObligation,
            ...conditions,
            birthLocation: {
                id: `/api/locations/${payload.generalInfo.birthLocationId}`,
                county: payload.generalInfo.department,
                city: payload.generalInfo.birthPlace,
                country: payload.generalInfo.nativeCountry,
            },
            currentLocation: {
                id: `/api/locations/${payload.address.id}`,
                country: payload.address.country,
                address: payload.address.address,
                zipCode: payload.address.zipCode,
                city: payload.address.city,
                formattedAddress: payload.address.formattedAddress,
            },
            taxLocation,
            politicallyExposedPerson: convertToBoolOrNull(
                payload.politicallyExposed.politicallyExposedPerson
            ),
            politicallyExposedPersonReason: JSON.stringify({
                choice: Object.keys(payload.politicallyExposed.politicallyExposedReason)
                    .filter(
                        key => payload.politicallyExposed.politicallyExposedReason[key] === true
                    )
                    .map(key => `step5.question_15.option_${key}`),
            }),
        },
    };
};

export const updateCosubscriber = payload => ({
    subscriptions: [{ coSubscriber: updateInvestorProfilePP(payload) }],
});

export const updateInvestorProfilePM = (payload, document) => {
    let docId = payload.corporateInfo?.legalRepresentativeDocument?.id || null;
    if (document?.[0] && document[0]?.id) {
        docId = document[0].id;
    }
    return {
        firstName: payload.corporateInfo.firstName,
        lastName: payload.corporateInfo.lastName,
        phoneNumber: payload.generalInfo.phoneNumber,
        title: payload.corporateInfo.title,
        birthName: payload.corporateInfo.birthName,
        email: payload.corporateInfo.email,
        investorProfile: {
            id: `/api/investor_profiles/${payload.generalInfo.id}`,
            fax: payload.generalInfo.fax || '',
            birthDate: payload.generalInfo.birthDate || null,
            nationality: payload.generalInfo.nationality,
            usPerson: payload.fiscalSituation.isUSPerson === 'ineligible',
            taxCountry: payload.fiscalSituation.taxResident,
            jobTitle: payload.situation.profession,
            activity: payload.situation.activitySector,
            birthLocation: {
                id: `/api/locations/${payload.generalInfo.birthLocationId}`,
                city: payload.generalInfo.birthPlace,
                county: payload.generalInfo.department,
                country: payload.generalInfo.nativeCountry,
            },
            currentLocation: {
                id: `/api/locations/${payload.address.id}`,
                address: payload.address.address,
                country: payload.address.country,
                zipCode: payload.address.zipCode,
                city: payload.address.city,
                formattedAddress: payload.address.formattedAddress,
            },
            politicallyExposedPerson: convertToBoolOrNull(
                payload.politicallyExposed.politicallyExposedPerson
            ),
            politicallyExposedPersonReason: JSON.stringify({
                choice: Object.keys(payload.politicallyExposed.politicallyExposedReason)
                    .filter(
                        key => payload.politicallyExposed.politicallyExposedReason[key] === true
                    )
                    .map(key => `step5.question_15.option_${key}`),
            }),
        },
        investorCompany: {
            id: `/api/investor_companies/${payload.corporateInfo.id}`,
            corporateName: payload.corporateInfo.corporateName,
            corporateForm: JSON.stringify({
                choice: payload.corporateInfo.corporateForm,
                ...(payload.corporateInfo.corporateForm === corporateForms.other && {
                    other: payload.corporateInfo.otherCorporateForm,
                }),
            }),
            activity: JSON.stringify({
                choice: payload.corporateInfo.activity,
                ...(payload.corporateInfo.activity === activities.other && {
                    other: payload.corporateInfo.otherActivity,
                }),
            }),
            rcsRegistrationAddress: payload.corporateInfo.rcsRegistrationAddress,
            shareCapital: +payload.corporateInfo.shareCapital,
            currency: payload.corporateInfo.currency,
            taxSystem: payload.corporateInfo.taxSystem,
            rcsNumber: payload.corporateInfo.rcsNumber,
            signatoryOnKbis: convertToBoolOrNull(
                payload.corporateInfo.signatoryOnKbis,
                'yes',
                'no'
            ),
            representativeName: payload.corporateInfo.representativeName,
            representativeRole: payload.corporateInfo.representativeRole,
            ownAccountAgreement: convertToBoolOrNull(
                payload.corporateInfo.ownAccountAgreement,
                'yes',
                'no'
            ),
            ...(docId && { legalRepresentativeDocument: `/api/documents/${docId}` }),
            addressLocation: {
                ...payload.corporateInfo.currentAddress,
                id: `/api/locations/${payload.corporateInfo.currentAddress.id}`,
            },
            legalEntityIdentificationNumber: payload.corporateInfo.legalEntityIdentificationNumber,
            postalLocation: payload.corporateInfo.hasDifferentPostalAddress
                ? {
                      ...payload.corporateInfo.postalAddress,
                      id: `/api/locations/${payload.corporateInfo.postalAddress.id}`,
                  }
                : {
                      id: `/api/locations/${payload.corporateInfo.postalAddress.id}`,
                      country: null,
                      address: '',
                      zipCode: '',
                      city: '',
                      formattedAddress: null,
                  },
        },
    };
};

export const updateInvestorProfile = obj => ({
    id: obj.id,
    [obj.emailsKey]: JSON.stringify(obj.emails),
});
